import { useState, useEffect } from 'react';
import { X, CheckCircle2, Palette, Video, Send, Sparkles, Hash, AlignLeft } from 'lucide-react';
import { sleep } from '../utils/typeSimulation';

// ── 30 days of content with real platform statuses ──────────────────────
const DAYS = [
  { d:1,  p:'IG', f:'Carousel', t:'E',  status:'done',     title:'Meet The Bloom Bakery — who we are' },
  { d:2,  p:'FB', f:'Post',     t:'P',  status:'done',     title:'Grand opening offer — 20% off this week' },
  { d:3,  p:'IG', f:'Reel',     t:'EN', status:'done',     title:'Behind the scenes — morning bake timelapse' },
  { d:4,  p:'FB', f:'Post',     t:'I',  status:'done',     title:'Why fresh ingredients matter every day' },
  { d:5,  p:'IG', f:'Carousel', t:'E',  status:'done',     title:'Our 5 signature pastries — a guide' },
  { d:6,  p:'IG', f:'Story',    t:'P',  status:'done',     title:'Weekend special — flat white + croissant deal' },
  { d:7,  p:'FB', f:'Post',     t:'E',  status:'done',     title:'The art of sourdough — how we make ours' },
  { d:8,  p:'IG', f:'Reel',     t:'EN', status:'done',     title:'Staff latte art challenge ☕' },
  { d:9,  p:'FB', f:'Post',     t:'P',  status:'done',     title:'Loyalty card now available — collect & save' },
  { d:10, p:'IG', f:'Carousel', t:'I',  status:'done',     title:'Customer stories — our regulars love us' },
  { d:11, p:'FB', f:'Post',     t:'E',  status:'done',     title:'Coffee brewing tips — V60 vs Aeropress' },
  { d:12, p:'IG', f:'Reel',     t:'EN', status:'done',     title:'Latte art slow-motion pour 🎨' },
  { d:13, p:'IG', f:'Story',    t:'P',  status:'done',     title:'Sunday brunch menu — book your table' },
  { d:14, p:'FB', f:'Post',     t:'I',  status:'done',     title:'Community spotlight — Manchester small biz' },
  { d:15, p:'IG', f:'Carousel', t:'I',  status:'today',    title:'Summer collection launch — shop now 🌞' },
  { d:16, p:'FB', f:'Post',     t:'E',  status:'edited',   title:'What makes The Bloom different from chains' },
  { d:17, p:'IG', f:'Reel',     t:'EN', status:'draft',    title:'Morning rush time-lapse — 7am to 9am' },
  { d:18, p:'FB', f:'Post',     t:'P',  status:'draft',    title:'Seasonal menu launch — new summer items' },
  { d:19, p:'IG', f:'Carousel', t:'E',  status:'draft',    title:'Ingredient spotlight — single origin beans' },
  { d:20, p:'IG', f:'Story',    t:'P',  status:'upcoming', title:'Flash sale — today only 25% off all drinks' },
  { d:21, p:'FB', f:'Post',     t:'I',  status:'upcoming', title:'Thank you Manchester — one month together' },
  { d:22, p:'IG', f:'Reel',     t:'EN', status:'upcoming', title:'Customer favourite drink revealed! 🏆' },
  { d:23, p:'FB', f:'Post',     t:'P',  status:'upcoming', title:'Refer a friend — earn a free coffee' },
  { d:24, p:'IG', f:'Carousel', t:'E',  status:'upcoming', title:'Catering packages — events & offices' },
  { d:25, p:'FB', f:'Post',     t:'I',  status:'upcoming', title:'One month in — what we\'ve learned together' },
  { d:26, p:'IG', f:'Reel',     t:'EN', status:'upcoming', title:'Team day out — we love Manchester 🐝' },
  { d:27, p:'IG', f:'Carousel', t:'P',  status:'upcoming', title:'End of month clearance — grab a deal' },
  { d:28, p:'FB', f:'Post',     t:'E',  status:'upcoming', title:'Preparing for autumn — what\'s coming next' },
  { d:29, p:'IG', f:'Story',    t:'I',  status:'upcoming', title:'30 days in numbers — our campaign wrap' },
  { d:30, p:'FB', f:'Post',     t:'P',  status:'upcoming', title:'Campaign finale — celebrate with us 🎉' },
];

// Exact status colours matching the real platform
const STATUS = {
  done:     { cell: 'bg-green-500',  text: 'text-white', dot: 'bg-green-500', label: 'Completed' },
  today:    { cell: 'bg-blue-600',   text: 'text-white', dot: 'bg-blue-500',  label: 'Today'     },
  edited:   { cell: 'bg-blue-200',   text: 'text-blue-800', dot: 'bg-blue-400', label: 'Edited'  },
  draft:    { cell: 'bg-gray-300',   text: 'text-gray-700', dot: 'bg-gray-400', label: 'Draft'   },
  upcoming: { cell: 'bg-slate-100',  text: 'text-slate-400', dot: 'bg-slate-300', label: 'Upcoming' },
};

const TYPE_BG = { E:'bg-blue-500', EN:'bg-purple-500', I:'bg-amber-500', P:'bg-green-500' };
const TYPE_LABEL = { E:'Educational', EN:'Entertainment', I:'Inspirational', P:'Promotional' };
const TONE_OPTS = ['Conversational', 'Inspirational', 'Educational', 'Professional', 'Promotional', 'Casual'];
const FORMAT_OPTS = ['Carousel', 'Reel', 'Post', 'Story'];

// Today's Day 15 content
const TODAY = {
  platform: 'Instagram',
  format: 'Carousel',
  contentType: 'Inspirational',
  tone: 'Conversational',
  brief: 'Announce the new summer collection with warmth and excitement. Showcase 3–4 best-selling items. Include a clear call-to-action to shop via link in bio. Keep it visual and relatable.',
  caption: '✨ Summer is finally here — and so is our brand-new collection!\n\nWe\'ve been busy behind the scenes creating something special just for you ☕🌞\n\nFrom seasonal drinks to fresh pastry specials, this is our most exciting menu yet.\n\nCome in and see what all the buzz is about — or shop online via the link in bio!\n\n📍 The Bloom Bakery, Manchester City Centre',
  hashtags: ['#TheBloomBakery', '#ManchesterCafe', '#SummerMenu', '#NewIn', '#CoffeeLover', '#CafeCulture', '#ShopLocal', '#Artisan', '#FreshBaked', '#Manchester'],
  charLimit: 2200,
};

const WORKFLOW_STEPS = [
  { id: 'brief',   icon: AlignLeft, label: 'Post Brief',      color: 'blue'   },
  { id: 'caption', icon: Sparkles,  label: 'AI Caption',      color: 'purple' },
  { id: 'hashtags',icon: Hash,      label: 'Hashtags',        color: 'green'  },
  { id: 'design',  icon: Palette,   label: 'Design Studio',   color: 'blue'   },
  { id: 'video',   icon: Video,     label: 'Video Workflow',  color: 'purple' },
  { id: 'publish', icon: Send,      label: 'Publish',         color: 'green'  },
];

export default function CalendarPanel({ onClose }) {
  const [selectedDay, setSelectedDay] = useState(15);
  const [captionText, setCaptionText] = useState('');
  const [captionDone, setCaptionDone] = useState(false);
  const [doneSteps, setDoneSteps] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedTone, setSelectedTone] = useState('Conversational');
  const [selectedFormat, setSelectedFormat] = useState('Carousel');

  const doneDays = DAYS.filter(d => d.status === 'done').length;

  useEffect(() => {
    const run = async () => {
      await sleep(500);

      // Brief
      setActiveStep(0);
      await sleep(1000);
      setDoneSteps(['brief']);
      setActiveStep(1);

      // Caption typing
      const cap = TODAY.caption;
      for (let i = 1; i <= cap.length; i++) {
        setCaptionText(cap.substring(0, i));
        await sleep(12);
      }
      setCaptionDone(true);
      await sleep(500);
      setDoneSteps(p => [...p, 'caption']);
      setActiveStep(2);
      await sleep(800);

      // Hashtags
      setDoneSteps(p => [...p, 'hashtags']);
      setActiveStep(3);
      await sleep(800);

      // Design
      setDoneSteps(p => [...p, 'design']);
      setActiveStep(4);
      await sleep(800);

      // Video (optional step — skip)
      setActiveStep(5);
      await sleep(500);
      setDoneSteps(p => [...p, 'video']);
    };
    run();
  }, []);

  const selDay = DAYS.find(d => d.day === selectedDay) || DAYS[14];

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-4xl bg-white shadow-2xl z-50 overflow-y-auto animate-slide-in">
        <div className="h-20 bg-white" />

        {/* ── Sticky header ── */}
        <div className="sticky top-20 bg-white border-b-2 border-gray-200 p-5 z-20">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-0.5">Design & Execution Calendar</h2>
              <p className="text-sm text-gray-500">30-day content plan — AI caption → Design Studio → direct publish</p>
            </div>
            <button onClick={onClose} className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all opacity-60 hover:opacity-100">
              <X size={24} />
            </button>
          </div>

          {/* Campaign progress */}
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-gray-700">{doneDays} of {DAYS.length} posts completed</span>
            <div className="flex items-center gap-3">
              {Object.entries(STATUS).filter(([k]) => ['done','edited','draft'].includes(k)).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1 text-xs text-gray-500">
                  <span className={`w-2 h-2 rounded-full ${v.dot}`} />{v.label}
                </span>
              ))}
            </div>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 rounded-full transition-all duration-700" style={{ width: `${(doneDays / DAYS.length) * 100}%` }} />
          </div>
        </div>

        <div className="p-5 space-y-6">

          {/* ── 30-Day Calendar Grid ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
              30-Day Content Calendar
              <span className="ml-auto text-xs text-gray-400 font-normal">Click a day to preview</span>
            </h3>
            <div className="grid grid-cols-10 gap-1.5">
              {DAYS.map(day => {
                const s = STATUS[day.status];
                const isSelected = selectedDay === day.day;
                return (
                  <button key={day.d} onClick={() => setSelectedDay(day.d)}
                    title={`Day ${day.d}: ${day.title}`}
                    className={`relative aspect-square rounded-lg text-[10px] font-bold flex flex-col items-center justify-center transition-all ${s.cell} ${s.text} ${
                      isSelected ? 'ring-2 ring-white ring-offset-1 ring-offset-gray-200 scale-110 shadow-md z-10' : 'hover:scale-105'
                    }`}
                  >
                    {day.status === 'done'
                      ? <CheckCircle2 size={11} />
                      : day.status === 'today'
                      ? <><span className="text-[8px] opacity-70">TODAY</span><span>{day.d}</span></>
                      : day.d}
                    {/* Content type dot */}
                    {day.status !== 'done' && (
                      <div className={`absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${TYPE_BG[day.t]}`} />
                    )}
                  </button>
                );
              })}
            </div>

            {/* Content type legend */}
            <div className="mt-2 flex flex-wrap gap-3">
              {Object.entries(TYPE_LABEL).map(([k, v]) => (
                <span key={k} className="flex items-center gap-1 text-xs text-gray-500">
                  <span className={`w-2 h-2 rounded-full ${TYPE_BG[k]}`} />
                  {v}
                </span>
              ))}
            </div>
          </div>

          {/* ── Selected Day Preview ── */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden">
            <div className={`px-4 py-2.5 flex items-center gap-3 ${
              selDay.status === 'done' ? 'bg-green-600' :
              selDay.status === 'today' ? 'bg-blue-600' :
              selDay.status === 'edited' ? 'bg-blue-200' : 'bg-gray-200'
            }`}>
              <span className={`text-sm font-bold ${selDay.status === 'done' || selDay.status === 'today' ? 'text-white' : 'text-gray-700'}`}>
                Day {selDay.d}
              </span>
              <span className={`${selDay.status === 'done' || selDay.status === 'today' ? 'text-white/80' : 'text-gray-500'} text-xs`}>·</span>
              <span className={`text-xs font-semibold ${selDay.status === 'done' || selDay.status === 'today' ? 'text-white/90' : 'text-gray-600'}`}>
                {selDay.title}
              </span>
              <span className="ml-auto">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${TYPE_BG[selDay.t]} text-white`}>
                  {TYPE_LABEL[selDay.t]}
                </span>
              </span>
            </div>
            <div className="p-3 flex flex-wrap gap-2">
              <InfoBadge label="Platform" value={selDay.p === 'IG' ? 'Instagram' : 'Facebook'} color={selDay.p === 'IG' ? 'pink' : 'blue'} />
              <InfoBadge label="Format" value={selDay.f} color="gray" />
              <InfoBadge label="Status" value={STATUS[selDay.status].label} color={selDay.status === 'done' ? 'green' : selDay.status === 'today' ? 'blue' : 'gray'} />
            </div>
          </div>

          {/* ── Today's Workflow (Day 15) ── */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Sparkles size={14} className="text-blue-600" />
              Day 15 — Live Creation Workflow
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full font-medium">Demo</span>
            </h3>

            {/* Workflow step indicators */}
            <div className="flex items-center mb-4 overflow-x-auto pb-1">
              {WORKFLOW_STEPS.map((ws, idx) => {
                const done = doneSteps.includes(ws.id);
                const active = activeStep === idx;
                return (
                  <div key={ws.id} className="flex items-center flex-shrink-0">
                    <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      done ? 'bg-green-100 text-green-700' :
                      active ? 'bg-blue-100 text-blue-700' : 'text-gray-400'
                    }`}>
                      <div className={`w-4 h-4 rounded-full flex items-center justify-center ${done ? 'bg-green-500' : active ? 'bg-blue-500 animate-pulse' : 'bg-gray-200'}`}>
                        {done ? <span className="text-[8px] text-white font-bold">✓</span> : null}
                      </div>
                      {ws.label}
                    </div>
                    {idx < WORKFLOW_STEPS.length - 1 && (
                      <div className={`w-4 h-0.5 mx-0.5 ${doneSteps.includes(ws.id) ? 'bg-green-300' : 'bg-gray-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* ── Post Brief ── */}
            <div className={`mb-3 border rounded-xl overflow-hidden transition-all ${doneSteps.includes('brief') ? 'border-green-200' : 'border-blue-200'}`}>
              <div className={`px-4 py-2 flex items-center justify-between ${doneSteps.includes('brief') ? 'bg-green-50' : 'bg-blue-50'}`}>
                <span className="text-xs font-bold text-gray-700 flex items-center gap-2">
                  <AlignLeft size={13} className={doneSteps.includes('brief') ? 'text-green-600' : 'text-blue-600'} />
                  Post Brief
                </span>
                {doneSteps.includes('brief') && <span className="text-xs text-green-600 font-semibold">✓ AI Generated</span>}
              </div>
              <div className="p-4 space-y-2">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-500 font-semibold mb-1 uppercase">Platform · Format</p>
                    <div className="flex gap-1.5">
                      <span className="text-xs px-2 py-0.5 bg-pink-100 text-pink-700 rounded font-semibold">{TODAY.platform}</span>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded font-semibold">{TODAY.format}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <p className="text-[10px] text-gray-500 font-semibold mb-1 uppercase">Tone · Type</p>
                    <div className="flex gap-1.5">
                      <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded font-semibold">{TODAY.tone}</span>
                      <span className="text-xs px-2 py-0.5 bg-amber-500 text-white rounded font-semibold">{TODAY.contentType}</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-700 leading-relaxed bg-white border border-gray-200 rounded-lg p-3">{TODAY.brief}</p>

                {/* Tone selector (real platform UI) */}
                <div>
                  <p className="text-[10px] text-gray-500 font-semibold mb-1.5 uppercase">Tone</p>
                  <div className="flex flex-wrap gap-1.5">
                    {TONE_OPTS.map(t => (
                      <button key={t} onClick={() => setSelectedTone(t)} className={`text-xs px-2.5 py-1 rounded-full border-2 font-semibold transition-all ${
                        selectedTone === t ? 'bg-blue-600 border-blue-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-blue-300'
                      }`}>{t}</button>
                    ))}
                  </div>
                </div>

                {/* Format selector */}
                <div>
                  <p className="text-[10px] text-gray-500 font-semibold mb-1.5 uppercase">Format</p>
                  <div className="flex gap-1.5">
                    {FORMAT_OPTS.map(fmt => (
                      <button key={fmt} onClick={() => setSelectedFormat(fmt)} className={`text-xs px-2.5 py-1 rounded-lg border-2 font-semibold transition-all ${
                        selectedFormat === fmt ? 'bg-blue-600 border-blue-600 text-white' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-blue-300'
                      }`}>{fmt}</button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ── AI Caption ── */}
            {(doneSteps.includes('brief') || activeStep >= 1) && (
              <div className={`mb-3 border rounded-xl overflow-hidden transition-all ${doneSteps.includes('caption') ? 'border-green-200' : 'border-purple-200'}`}>
                <div className={`px-4 py-2 flex items-center justify-between ${doneSteps.includes('caption') ? 'bg-green-50' : 'bg-purple-50'}`}>
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-2">
                    <Sparkles size={13} className={doneSteps.includes('caption') ? 'text-green-600' : 'text-purple-600'} />
                    AI Caption
                  </span>
                  <div className="flex items-center gap-2">
                    {captionDone && (
                      <span className="text-xs text-gray-500 font-mono">{captionText.length}/{TODAY.charLimit} chars</span>
                    )}
                    {doneSteps.includes('caption') && <span className="text-xs text-green-600 font-semibold">✓ Generated</span>}
                  </div>
                </div>
                <div className="p-4">
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-800 leading-relaxed whitespace-pre-line font-mono min-h-[80px]">
                    {captionText}
                    {!captionDone && <span className="animate-pulse text-blue-600 font-thin">|</span>}
                  </div>
                </div>
              </div>
            )}

            {/* ── Hashtags ── */}
            {doneSteps.includes('caption') && (
              <div className={`mb-3 border rounded-xl overflow-hidden transition-all ${doneSteps.includes('hashtags') ? 'border-green-200' : 'border-green-200'}`}>
                <div className="px-4 py-2 flex items-center justify-between bg-green-50">
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-2">
                    <Hash size={13} className="text-green-600" />
                    Hashtags
                  </span>
                  <span className="text-xs text-gray-500">{TODAY.hashtags.length}/30 hashtags · Instagram</span>
                </div>
                <div className="p-3 flex flex-wrap gap-1.5">
                  {TODAY.hashtags.map((h, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 bg-green-50 border border-green-200 text-green-700 rounded-full font-medium">{h}</span>
                  ))}
                </div>
              </div>
            )}

            {/* ── Design Studio ── */}
            {doneSteps.includes('hashtags') && (
              <div className={`mb-3 border rounded-xl overflow-hidden transition-all ${doneSteps.includes('design') ? 'border-blue-200' : 'border-blue-300'}`}>
                <div className={`px-4 py-2 flex items-center justify-between ${doneSteps.includes('design') ? 'bg-blue-50' : 'bg-blue-100'}`}>
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-2">
                    <Palette size={13} className="text-blue-600" />
                    Design Studio
                  </span>
                  {doneSteps.includes('design') && <span className="text-xs text-blue-600 font-semibold">✓ Template selected</span>}
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {[
                      { name: 'Bold Overlay',  bg: 'bg-slate-800', text: 'text-white'  },
                      { name: 'Split Layout',  bg: 'bg-blue-600',  text: 'text-white'  },
                      { name: 'Clean Minimal', bg: 'bg-white border-2 border-gray-200', text: 'text-gray-800' },
                      { name: 'Top Band',      bg: 'bg-amber-500', text: 'text-white'  },
                    ].map((tpl, i) => (
                      <div key={tpl.name} className={`${tpl.bg} rounded-lg aspect-square flex items-end p-1.5 cursor-pointer ${i === 0 ? 'ring-2 ring-blue-500 ring-offset-1' : ''} hover:scale-105 transition-all`}>
                        <span className={`text-[8px] font-bold ${tpl.text} truncate`}>{tpl.name}</span>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-center">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg py-2 px-1">
                      <p className="font-bold text-gray-700">Aspect Ratio</p>
                      <p className="text-blue-600 font-semibold">1:1 Square</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg py-2 px-1">
                      <p className="font-bold text-gray-700">AI Background</p>
                      <p className="text-blue-600 font-semibold">6 Styles</p>
                    </div>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg py-2 px-1">
                      <p className="font-bold text-gray-700">Text Styles</p>
                      <p className="text-blue-600 font-semibold">14 Fonts</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Publish ── */}
            {activeStep >= 5 && (
              <div className="border border-green-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-green-50 flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700 flex items-center gap-2">
                    <Send size={13} className="text-green-600" />
                    Publish to Social
                  </span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button className="flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-opacity">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0z"/></svg>
                      Post to Instagram
                    </button>
                    <button className="flex items-center justify-center gap-2 py-2.5 bg-blue-600 rounded-lg text-white text-sm font-semibold hover:bg-blue-700 transition-colors">
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      Post to Facebook
                    </button>
                  </div>
                  <div className="flex items-center justify-center gap-2 py-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-xs text-green-700 font-semibold">Instagram & Facebook connected</span>
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>

        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <p className="text-xs text-gray-400 text-center">Simulated demo — In the real platform, every day has a full post brief, AI caption, Design Studio, video workflow, and direct social publishing.</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
      `}</style>
    </>
  );
}

function InfoBadge({ label, value, color }) {
  const c = color === 'pink' ? 'bg-pink-50 text-pink-700 border-pink-200'
          : color === 'blue' ? 'bg-blue-50 text-blue-700 border-blue-200'
          : color === 'green' ? 'bg-green-50 text-green-700 border-green-200'
          : 'bg-gray-50 text-gray-700 border-gray-200';
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${c}`}>
      <span className="text-gray-500">{label}:</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}
